import { useState } from 'react';
import { toast } from 'react-toastify';
import { getCashSession, openCashSession, type CashSession } from '../../../services/CashService';
import { updateOrderStatus } from '../../../services/OrderService';
import { createPayment } from '../../../services/PaymentService';
import { buildReceiptData, type ReceiptData } from '../../../utils/receipt';
import { OfflineQueueService } from '../../../services/OfflineQueueService';
import type { Order } from '../../../types/@types/order';
import type { Table } from '../../../types/@types/table';
import type { PaymentDetail } from '../../../components/CashRegister/PaymentModal';

interface UsePosTransactionsProps {
  isTakeawayMode: boolean;
  activeOrder: Order | null;
  activeOrderTotal: number;
  activeOrderItems: any[];
  activeOrderTotals: any;
  selected: Table | null;
  tables: Table[] | undefined;
  user: any;
  orderCustomer: { customerName: string; customerPhone: string };

  cancelOrderMutation: any;
  unjoinTableMutation: any;
  updateTableMutation: any;

  setCancelModalOpen: (v: boolean) => void;
  setActiveOrderId: (id: string | number | null) => void;
  setCart: (cart: any) => void;
  refetchActiveOrder: () => void;
  refetchTables: () => void;
  setSelected: (table: Table | null) => void;
}

export function usePosTransactions(props: UsePosTransactionsProps) {
  const [cashSession, setCashSession] = useState<CashSession | null>(null);
  const [showOpenCash, setShowOpenCash] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [receiptToPrint, setReceiptToPrint] = useState<ReceiptData | null>(null);
  const [openingCash, setOpeningCash] = useState(false);

  const handlePayClick = async () => {
    if (props.isTakeawayMode) return;
    // Permit charging any active order regardless of its production status, as long as it has a positive total
    if (!props.activeOrder) return;
    const isAlreadyPaid = props.activeOrder.payment_status === 'PAID' || props.activeOrder.paid === true;
    const isCancelled = props.activeOrder.status?.toUpperCase() === 'CANCELLED';
    if (isAlreadyPaid || isCancelled || props.activeOrderTotal <= 0) return;
    try {
      const sess = await getCashSession();
      if (!sess) {
        setShowOpenCash(true);
      } else {
        setCashSession(sess);
        setShowPayment(true);
      }
    } catch (err: any) {
      if (err?.isOffline) {
        toast.warn('Sin conexión. Abre la caja cuando vuelva la red.');
      } else {
        toast.error('No se pudo obtener la sesión de caja');
      }
    }
  };

  const handleCancelActiveOrder = (_reason?: string) => {
    if (props.isTakeawayMode || !props.activeOrder?.id) return;
    props.cancelOrderMutation.mutate(
      { orderId: props.activeOrder.id, tableId: props.selected?.id },
      {
        onSuccess: () => {
          props.setCancelModalOpen(false);
          props.setActiveOrderId(null);
          props.setCart({});
        },
      },
    );
  };

  const handleOpenSessionConfirm = async (amount: number) => {
    if (props.isTakeawayMode || !props.user) return;
    setOpeningCash(true);
    try {
      const sess = await openCashSession({ user: props.user.id, initialAmount: amount });
      setCashSession(sess);
      setShowOpenCash(false);
      setShowPayment(true);
    } catch (err: any) {
      if (err.response?.data?.message) {
        toast.error(err.response.data.message);
      } else {
        toast.error('No se pudo abrir la caja');
      }
    } finally {
      setOpeningCash(false);
    }
  };

  const handleConfirmPayment = async (payments: PaymentDetail[]) => {
    if (props.isTakeawayMode || !cashSession || !props.activeOrder || !props.selected) return;

    const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
    if (totalPaid < props.activeOrderTotal) {
      toast.warn('Monto insuficiente');
      return;
    }

    const totalReceived = payments.reduce((sum, p) => sum + p.received, 0);
    const change = totalReceived - props.activeOrderTotal;
    const paymentTimestamp = new Date().toISOString();
    let receiptPayload: ReceiptData | null = null;

    setProcessingPayment(true);

    const isOffline = !navigator.onLine;

    // ── OFFLINE PATH ───────────────────────────────────────────────────────────
    // Queue payments and status update; generate receipt locally so the cashier
    // can print/see the ticket even without connectivity.
    if (isOffline) {
      try {
        for (const p of payments) {
          await OfflineQueueService.pushOperation('CREATE_PAYMENT', {
            orderId: props.activeOrder.id,
            tableId: props.selected.id,
            amount: p.amount,
            method: p.method,
            cashRegisterId: cashSession.id,
            ...(p.received > p.amount ? { received: p.received, change: p.received - p.amount } : {}),
          });
        }
        await OfflineQueueService.pushOperation('UPDATE_ORDER_STATUS', {
          id: props.activeOrder.id,
          status: 'PAID',
        });

        receiptPayload = buildReceiptData({
          order: props.activeOrder,
          items: props.activeOrderItems.map((item) => ({
            name: item.name || item.product?.name || 'Producto',
            quantity: item.quantity ?? 0,
            unitPrice: item.unitPrice ?? item.base_price ?? 0,
            total: item.line_subtotal ?? (item.unitPrice ?? item.base_price ?? 0) * (item.quantity ?? 0),
            notes: item.kitchenSummary || item.notes,
          })),
          totals: props.activeOrderTotals,
          table: props.selected,
          customerName: props.orderCustomer.customerName,
          received: totalReceived,
          change,
          paidAt: paymentTimestamp,
        });

        localStorage.removeItem(`pos:activeOrder:${props.selected.id}`);
        props.setCart({});
        props.setActiveOrderId(null);
        props.setSelected(null);
        toast.warn(`Venta guardada OFFLINE — Cambio: $${change.toLocaleString()}. Se sincronizará al recuperar internet.`);
      } catch (e) {
        toast.error('Error al guardar la venta offline');
      } finally {
        setProcessingPayment(false);
        setShowPayment(false);
        if (receiptPayload) setReceiptToPrint(receiptPayload);
      }
      return;
    }

    // ── ONLINE PATH ────────────────────────────────────────────────────────────
    try {
      await Promise.all(
        payments.map((p) =>
          createPayment({
            orderId: props.activeOrder!.id,
            tableId: props.selected!.id,
            amount: p.amount,
            method: p.method,
            cashRegisterId: cashSession.id,
            ...(p.received > p.amount ? { received: p.received, change: p.received - p.amount } : {}),
          })
        )
      );
    } catch {
      toast.error('No se pudo procesar uno o más pagos parciales');
      setProcessingPayment(false);
      return;
    }

    let statusUpdated = false;
    for (let i = 0; i < 3 && !statusUpdated; i++) {
      try {
        await updateOrderStatus(props.activeOrder.id, 'PAID');
        statusUpdated = true;
      } catch {
        if (i < 2) toast.error('Reintentando actualizar estado...');
      }
    }

    if (!statusUpdated) {
      setProcessingPayment(false);
      return;
    }


      // Backend ya se encarga de liberar la mesa y las mesas unidas si no hay más pedidos activos
      // (a través de OrderService::checkAndFreeTable y el evento TableUpdated)

      receiptPayload = buildReceiptData({
        order: props.activeOrder,
        items: props.activeOrderItems.map((item) => ({
          name: item.name || item.product?.name || 'Producto',
          quantity: item.quantity ?? 0,
          unitPrice: item.unitPrice ?? item.base_price ?? 0,
          total: item.line_subtotal ?? (item.unitPrice ?? item.base_price ?? 0) * (item.quantity ?? 0),
          notes: item.kitchenSummary || item.notes,
        })),
        totals: props.activeOrderTotals,
        table: props.selected,
        customerName: props.orderCustomer.customerName,
        received: totalReceived,
        change,
        paidAt: paymentTimestamp,
      });

      localStorage.removeItem(`pos:activeOrder:${props.selected.id}`);
      props.setCart({});
      toast.success(`Venta registrada - Cambio: $${change.toLocaleString()}`);
      props.setActiveOrderId(null);
      props.refetchActiveOrder();
      props.refetchTables();
      props.setSelected(null);
      setProcessingPayment(false);
      setShowPayment(false);
      if (receiptPayload) {
        setReceiptToPrint(receiptPayload);
      }
  };

  return {
    cashSession,
    showOpenCash,
    setShowOpenCash,
    showPayment,
    setShowPayment,
    processingPayment,
    receiptToPrint,
    setReceiptToPrint,
    openingCash,
    handlePayClick,
    handleCancelActiveOrder,
    handleOpenSessionConfirm,
    handleConfirmPayment,
  };
}
